import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { CheckCircle2, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getGa4Ids } from '../../utils/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PACKAGES = ['Launch', 'Grow', 'Scale', 'Dominate', 'Not sure yet'];

const inputClass =
  'w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-sm font-medium text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:border-blue-600 focus:bg-white transition-colors';

export const DiscoveryModal = ({ open, onOpenChange, preselectedPackage, market = 'uk' }) => {
  const [form, setForm] = useState({ name: '', business: '', email: '', phone: '', message: '' });
  const [pkg, setPkg] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setPkg(preselectedPackage || null);
      setSuccess(false);
    }
  }, [open, preselectedPackage]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.business || !form.email || !form.phone) {
      toast.error('Please fill in your name, business, email and phone.');
      return;
    }
    setSubmitting(true);
    try {
      const gaIds = await getGa4Ids();
      const res = await fetch(`${API_URL}/api/enquiries/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, package_interest: pkg, source: 'discovery_call', market, ...gaIds }),
      });
      if (!res.ok) throw new Error('Request failed');
      setSuccess(true);
      setForm({ name: '', business: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error('Something went wrong. Please try again or email hello@brightlabs.live');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="agency-page max-w-lg bg-white border border-zinc-200 text-zinc-950 rounded-3xl p-0 overflow-hidden gap-0 shadow-2xl" data-testid="discovery-modal">
        <DialogTitle className="sr-only">Book a discovery call</DialogTitle>
        {success ? (
          <div className="p-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center mb-5">
              <CheckCircle2 className="text-blue-600" size={26} />
            </div>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-2">You're booked in.</h3>
            <p className="text-sm text-zinc-500 leading-relaxed mb-7 font-medium">
              We'll reach out within 24 hours to schedule your discovery call. No pitch decks. Just a straight conversation about your growth.
            </p>
            <button
              data-testid="discovery-success-close"
              onClick={() => onOpenChange(false)}
              className="bg-zinc-950 text-white hover:bg-zinc-800 transition-colors px-7 py-3 rounded-full font-semibold text-sm active:scale-95"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-8">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-blue-600 mb-2">Discovery Call</p>
            <h3 className="font-heading text-2xl font-bold tracking-tight mb-1">Let's talk growth.</h3>
            <p className="text-sm text-zinc-500 mb-6 font-medium">15 minutes. No obligation. We'll tell you exactly what we'd build.</p>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <input data-testid="discovery-name-input" className={inputClass} placeholder="Your name" value={form.name} onChange={set('name')} />
              <input data-testid="discovery-business-input" className={inputClass} placeholder="Business name" value={form.business} onChange={set('business')} />
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input data-testid="discovery-email-input" type="email" className={inputClass} placeholder="Email" value={form.email} onChange={set('email')} />
              <input data-testid="discovery-phone-input" type="tel" className={inputClass} placeholder="Phone" value={form.phone} onChange={set('phone')} />
            </div>

            <p className="text-xs font-bold text-zinc-500 mb-2 mt-5">What are you interested in?</p>
            <div className="flex flex-wrap gap-2 mb-5">
              {[...PACKAGES, ...(pkg && !PACKAGES.includes(pkg) ? [pkg] : [])].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`discovery-package-${p.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => setPkg(p)}
                  className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
                    pkg === p
                      ? 'bg-zinc-950 text-white border-zinc-950'
                      : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400 hover:text-zinc-950'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>

            <textarea
              data-testid="discovery-message-input"
              className={`${inputClass} resize-none mb-6`}
              rows={3}
              placeholder="Tell us about your business (optional)"
              value={form.message}
              onChange={set('message')}
            />

            <button
              type="submit"
              data-testid="discovery-submit-button"
              disabled={submitting}
              className="w-full bg-zinc-950 text-white hover:bg-zinc-800 transition-colors px-6 py-3.5 rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 active:scale-[0.99]"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Book My Discovery Call <ArrowRight size={15} /></>}
            </button>
            <p className="text-[11px] text-zinc-400 text-center mt-4 font-medium">We respond within 24 hours. Your details stay private.</p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
